<template>
  <div class="container">
    <HeadingOne class="-margin-md">入力内容のご確認</HeadingOne>

    <errorsAlert :errors="errors"></errorsAlert>

    <WizardGroup class="_mb-sm-35 _mb-md-50">
      <MainWizard :active-index="1" type="entry"></MainWizard>
    </WizardGroup>

    <p>
      ご入力内容をご確認のうえ、最下部にある「口座のご登録へ進む」ボタンを押して、お申し込み手続きを進めてください。
    </p>
    <p class="important">
      氏名（漢字、フリガナ）など誤りが無いか、必ずご確認ください。
    </p>

    <section>
      <HeadingTwo>お客さま情報</HeadingTwo>
      <TableGroup>
        <TableBox class="-entry">
          <TableBody>
            <TableRow>
              <TableHeader class="-entry"
                >氏名 <small>（漢字）</small>
              </TableHeader>
              <TableData>
                {{ displayNameKanji(userData) }}
              </TableData>
            </TableRow>
            <TableRow>
              <TableHeader class="-entry"
                >氏名 <small>（フリガナ）</small>
              </TableHeader>
              <TableData>
                {{ displayNameKana(userData) }}
              </TableData>
            </TableRow>
            <TableRow>
              <TableHeader class="-entry"> 生年月日 </TableHeader>
              <TableData>
                <label>{{ displayBth(userData.bth) }}</label>
              </TableData>
            </TableRow>
            <TableRow>
              <TableHeader class="-entry"> 性別 </TableHeader>
              <TableData>
                {{ displaySex(userData.sex) }}
              </TableData>
            </TableRow>
            <TableRow v-if="showAuPhone">
              <TableHeader class="-entry"
                >au携帯電話番号または<br />UQ mobile 電話番号
              </TableHeader>
              <TableData>
                {{
                  displayPhoneAu(
                    userData.phone_au_1,
                    userData.phone_au_2,
                    userData.phone_au_3
                  )
                }}
              </TableData>
            </TableRow>
            <TableRow v-if="!showAuPhone">
              <TableHeader class="-entry"
                >電話番号<small>（その他）</small>
              </TableHeader>
              <TableData>
                {{
                  displayPhoneMobile(
                    hasPhoneMobile,
                    userData.phone_mobile_1,
                    userData.phone_mobile_2,
                    userData.phone_mobile_3
                  )
                }}
              </TableData>
            </TableRow>
            <template v-if="showDefaultMail">
              <TableRow>
                <TableHeader class="-entry" rowspan="3"
                  >メールアドレス
                </TableHeader>
                <TableData>
                  {{ displayEzMail(userData.ez_mail) }}
                </TableData>
              </TableRow>
              <TableRow>
                <TableData>
                  {{ displayMail1(userData.mail_1) }}
                </TableData>
              </TableRow>
              <TableRow>
                <TableData>
                  {{ displayMail2(userData.mail_2) }}
                </TableData>
              </TableRow>
            </template>
            <TableRow v-if="showLoanMail">
              <TableHeader class="-entry">メールアドレス </TableHeader>
              <TableData>
                {{ userData.loan_mail }}
              </TableData>
            </TableRow>
            <TableRow>
              <TableHeader class="-entry">キャンペーンメール受信 </TableHeader>
              <TableData>
                {{ displayMailNotice }}
              </TableData>
            </TableRow>
          </TableBody>
          <!-- TableBody -->
        </TableBox>

        <TableBox class="-entry">
          <TableBody>
            <TableRow>
              <TableHeader class="-entry">暗証番号 </TableHeader>
              <TableData>**** </TableData>
            </TableRow>
          </TableBody>
          <!-- TableBody -->
        </TableBox>

        <TableBox class="-entry">
          <TableBody>
            <TableRow>
              <TableHeader class="-entry"> 運転免許証の有無 </TableHeader>
              <TableData>
                {{ displayDriverLicenseFlg }}
              </TableData>
            </TableRow>
            <TableRow v-if="showDriverLicenseNum">
              <TableHeader class="-entry"> 運転免許証番号 </TableHeader>
              <TableData>
                {{ userData.driver_license_num }}
              </TableData>
            </TableRow>
          </TableBody>
          <!-- TableBody -->
        </TableBox>

        <HeadingTwo class="_mt-sm-60">ご自宅情報</HeadingTwo>
        <TableBox class="-entry">
          <TableBody>
            <TableRow>
              <TableHeader class="-entry"> 郵便番号 </TableHeader>
              <TableData>
                〒{{ displayPostcode(userData.postcode) }}
              </TableData>
            </TableRow>
            <TableRow>
              <TableHeader class="-entry">
                ご住所 <small>（漢字）</small>
              </TableHeader>
              <TableData>
                {{
                  displayAddress(
                    userData.address_region,
                    userData.address_city,
                    userData.address_street,
                    userData.address_building
                  )
                }}
              </TableData>
            </TableRow>
            <TableRow>
              <TableHeader class="-entry">
                ご住所 <small>（フリガナ）</small>
              </TableHeader>
              <TableData>
                {{ userData.address_kana }}
              </TableData>
            </TableRow>
            <TableRow>
              <TableHeader class="-entry"> お住まいの種類 </TableHeader>
              <TableData>
                {{ displayHomeType(userData.home_type) }}
              </TableData>
            </TableRow>
          </TableBody>
          <!-- TableBody -->
        </TableBox>

        <TableBox class="-entry">
          <TableBody>
            <TableRow>
              <TableHeader class="-entry"> 居住年数 </TableHeader>
              <TableData>
                {{ displayJkyNgt(userData.nky_ngt) }}
              </TableData>
            </TableRow>
            <TableRow>
              <TableHeader class="-entry">
                住宅ローンあるいは家賃のお支払い<small>（月額）</small>
              </TableHeader>
              <TableData>
                {{ displayRentLoanAmount(userData.rent_loan_amount) }}
              </TableData>
            </TableRow>
            <!-- row -->
          </TableBody>
          <!-- TableBody -->
        </TableBox>

        <TableBox class="-entry">
          <TableBody>
            <TableRow>
              <TableHeader class="-entry">
                ご家族<small>（配偶者さま）</small>
              </TableHeader>
              <TableData>
                {{ displayMarriageStatus(userData.marriage_status) }}
              </TableData>
            </TableRow>
            <TableRow>
              <TableHeader class="-entry">
                ご家族<small>（お子さま）</small>
              </TableHeader>
              <TableData>
                {{ displayNumberOfChildren(userData.number_of_children) }}
              </TableData>
            </TableRow>
          </TableBody>
          <!-- TableBody -->
        </TableBox>

        <BtngroupSingle class="-marginbottom">
          <BtnBack
            class="-back -md"
            router-name="en002"
            tracking-name="en005_edit_button_1"
          >
            修正する
          </BtnBack>
        </BtngroupSingle>

        <HeadingTwo class="_mt-sm-60">お勤め先情報</HeadingTwo>
        <TableBox class="-entry">
          <TableBody>
            <TableRow>
              <TableHeader class="-entry"> 職業 </TableHeader>
              <TableData>
                {{ displayWorkForm(userData.work_form) }}
              </TableData>
            </TableRow>
            <TableRow>
              <TableHeader class="-entry"> 業種 </TableHeader>
              <TableData>
                {{ displayBizType(userData.biz_type) }}
              </TableData>
            </TableRow>
            <TableRow>
              <TableHeader class="-entry"> 職種 </TableHeader>
              <TableData>
                {{ displayJobType(userData.job_type) }}
              </TableData>
            </TableRow>
            <TableRow>
              <TableHeader class="-entry"> お勤め先 </TableHeader>
              <TableData>
                {{ userData.office_nm }}
              </TableData>
            </TableRow>
            <TableRow>
              <TableHeader class="-entry"> お勤め先電話番号 </TableHeader>
              <TableData>
                {{
                  displayOfficePhone(
                    userData.office_phone_1,
                    userData.office_phone_2,
                    userData.office_phone_3
                  )
                }}
              </TableData>
            </TableRow>
            <TableRow>
              <TableHeader class="-entry"> 所属部署 </TableHeader>
              <TableData>
                {{ userData.office_depart }}
              </TableData>
            </TableRow>
            <TableRow>
              <TableHeader class="-entry"> 入社年月 </TableHeader>
              <TableData>
                {{ displayNshNgt(userData.nsh_ngt) }}
              </TableData>
            </TableRow>
            <TableRow>
              <TableHeader class="-entry"> 社員数 </TableHeader>
              <TableData>
                {{ displayStaffNum(userData.staff_num) }}
              </TableData>
            </TableRow>
            <TableRow>
              <TableHeader class="-entry">
                年収 <small>（税込）</small>
              </TableHeader>
              <TableData>
                {{ displayGrossSalary(userData.gross_salary) }}
              </TableData>
            </TableRow>
          </TableBody>
          <!-- TableBody -->
        </TableBox>

        <BtngroupSingle class="-marginbottom">
          <BtnBack
            class="-back -md"
            router-name="en003"
            tracking-name="en005_edit_button_2"
          >
            修正する
          </BtnBack>
        </BtngroupSingle>

        <HeadingTwo class="_mt-sm-60">他社のお借り入れ状況</HeadingTwo>
        <TableBox class="-entry">
          <TableBody>
            <TableRow>
              <TableHeader class="-entry">
                他社の<br class="-spnone" />お借り入れ状況
                <small>（件数）</small>
              </TableHeader>
              <TableData>
                {{ displayotherLoanCount }}
              </TableData>
            </TableRow>
            <TableRow>
              <TableHeader class="-entry">
                他社の<br class="-spnone" />お借り入れ状況
                <small>（金額）</small>
              </TableHeader>
              <TableData>
                {{ displayOtherLoanAmount }}
              </TableData>
            </TableRow>
          </TableBody>
          <!-- TableBody -->
        </TableBox>

        <TableBox class="-entry">
          <TableBody>
            <TableRow>
              <TableHeader class="-entry"> ご希望のご利用限度額 </TableHeader>
              <TableData>
                {{ displayAskingLoanAmount }}
              </TableData>
            </TableRow>
            <TableRow>
              <TableHeader class="-entry"> 毎月のご返済日 </TableHeader>
              <TableData>
                {{ displayRepaymentDate(userData) }}
              </TableData>
            </TableRow>
          </TableBody>
          <!-- TableBody -->
        </TableBox>

        <TableBox class="-entry">
          <TableBody>
            <TableRow>
              <TableHeader class="-entry"> お取引の目的 </TableHeader>
              <TableData>
                <InputLabel
                  >本サービスは生計費融資となり、事業費融資を目的としたお申し込みはお受けできません。</InputLabel
                >
                {{ userData.purpose_of_use }}
              </TableData>
            </TableRow>
          </TableBody>
          <!-- TableBody -->
        </TableBox>

        <BtngroupSingle class="_mb-sm-40">
          <BtnBack
            class="-back -md"
            router-name="en004"
            tracking-name="en005_edit_button_3"
          >
            修正する
          </BtnBack>
        </BtngroupSingle>

        <form novalidate @submit.prevent="submitProfile">
          <BtngroupSingle class="-margin-lg">
            <BtnMain
              class="-main -lg confirmation"
              tracking-name="en005_next_button"
              onmousedown="dlpoloan_moushikomiuketsukeClickCv()"
              @click.prevent="submitProfile"
            >
              入力を完了して、<br class="-pcnone" />「口座のご登録」へ進む
            </BtnMain>
            <p class="btn-txt">
              <span class="important"
                >まだお申し込み手続きは完了しておりません。</span
              ><br />ご入力内容をご確認のうえ、上記「口座のご登録」へ進むボタンを押して、お申し込み手続きを進めてください。
            </p>
          </BtngroupSingle>
        </form>
      </TableGroup>
    </section>
  </div>
</template>

<script>
import { initCredentials } from "@/utils/Auth";
import {
  getUserDataLambdaInvoke,
  submitAttributesLambdaInvoke,
} from "@/utils/LambdaHelper";
import { driverLicenseFlgs, askingLoanAmounts } from "@/constants/constant";
import { setTrackingEventDataSetting } from "@/utils/TrackingDataHelper";
import { setMeta } from "@/utils/Meta";
import { goToErrorPage, scrollTop } from "@/utils/Other";
import {
  displayNameKanji,
  displayNameKana,
  displayBth,
  displaySex,
  displayPhoneAu,
  displayPhoneMobile,
  displayEzMail,
  displayMail1,
  displayMail2,
  displayPostcode,
  displayAddress,
  displayHomeType,
  displayJkyNgt,
  displayRentLoanAmount,
  displayMarriageStatus,
  displayNumberOfChildren,
  displayWorkForm,
  displayBizType,
  displayJobType,
  displayOfficePhone,
  displayNshNgt,
  displayStaffNum,
  displayGrossSalary,
  displayRepaymentDate,
  setHasPhoneMobile,
} from "@/utils/UserData";

import HeadingOne from "@/components/heading/HeadingOne";
import errorsAlert from "@/components/alert/ErrorsAlert";
import BtnMain from "@/components/button/BtnMain";
import BtnBack from "@/components/button/BtnBack";
import BtngroupSingle from "@/components/button/BtngroupSingle";
import HeadingTwo from "@/components/heading/HeadingTwo";
import TableBox from "@/components/tables/TableBox";
import TableBody from "@/components/tables/TableBody";
import TableRow from "@/components/tables/TableRow";
import TableHeader from "@/components/tables/TableHeader";
import TableData from "@/components/tables/TableData";
import InputLabel from "@/components/input/InputLabel";
import MainWizard from "@/components/wizard/MainWizard";
import WizardGroup from "@/components/wizard/WizardGroup";
import TableGroup from "@/components/tables/TableGroup";

export default {
  name: "En005",
  components: {
    TableGroup,
    WizardGroup,
    MainWizard,
    HeadingTwo,
    BtngroupSingle,
    errorsAlert,
    HeadingOne,
    BtnMain,
    BtnBack,
    InputLabel,
    TableData,
    TableHeader,
    TableRow,
    TableBody,
    TableBox,
  },
  setup() {
    setMeta({
      title: "入力内容のご確認",
      description:
        "au PAY スマートローンはau IDをお持ちのお客様のための便利なカードレスローンサービスです。お申込みは、スマートフォン・パソコンでかんたんWEB完結！24時間お申込み可能。最短30分で審査結果をご連絡します。",
      keywords:
        "au PAY スマートローン,au,カードレスローン,スマートフォン,au PAY 残高,セブン銀行スマホATM,借入,返済,手続き,入会,お借り入れ状況",
    });
    return {
      displayNameKanji,
      displayNameKana,
      displayBth,
      displaySex,
      displayPhoneAu,
      displayPhoneMobile,
      displayEzMail,
      displayMail1,
      displayMail2,
      displayPostcode,
      displayAddress,
      displayHomeType,
      displayJkyNgt,
      displayRentLoanAmount,
      displayMarriageStatus,
      displayNumberOfChildren,
      displayWorkForm,
      displayBizType,
      displayJobType,
      displayOfficePhone,
      displayNshNgt,
      displayStaffNum,
      displayGrossSalary,
      displayRepaymentDate,
      setHasPhoneMobile,
    };
  },
  data() {
    return {
      errors: null,
      isDisabled: false,
      hasPhoneMobile: false,
      userData: {
        // お客さま情報
        lastname_kanji: "",
        firstname_kanji: "",
        lastname_kana: "",
        firstname_kana: "",
        bth: "",
        sex: "",
        phone_au_1: "",
        phone_au_2: "",
        phone_au_3: "",
        phone_mobile_1: "",
        phone_mobile_2: "",
        phone_mobile_3: "",
        ez_mail: "",
        mail_1: "",
        mail_2: "",
        loan_mail: "",
        notice_flg: "",
        driver_license_flg: "",
        driver_license_num: "",
        // ご自宅情報
        postcode: "",
        address_region: "",
        address_city: "",
        address_street: "",
        address_building: "",
        address_kana: "",
        home_type: "",
        nky_ngt: "",
        rent_loan_amount: "",
        marriage_status: "",
        number_of_children: "",
        // お勤め先情報
        work_form: "",
        biz_type: "",
        job_type: "",
        office_nm: "",
        office_phone_1: "",
        office_phone_2: "",
        office_phone_3: "",
        office_depart: "",
        nsh_ngt: "",
        staff_num: "",
        gross_salary: "",
        // 他社のお借り入れ状況
        other_loan_count: "",
        other_loan_amount: "",
        asking_loan_amount: "",
        repayment_date: "", // 26日固定
        purpose_of_use: "",
      },
    };
  },
  computed: {
    showAuPhone() {
      return (
        this.userData.phone_au_1 &&
        this.userData.phone_au_2 &&
        this.userData.phone_au_3
      );
    },
    showLoanMail() {
      return (
        !this.userData.ez_mail && !this.userData.mail_1 && !this.userData.mail_2
      );
    },
    showDefaultMail() {
      return !this.showLoanMail;
    },
    displayMailNotice() {
      return this.userData.notice_flg ? "同意する" : "同意しない";
    },
    displayDriverLicenseFlg() {
      for (let driverLicenseFlg of driverLicenseFlgs) {
        if (driverLicenseFlg.value === this.userData.driver_license_flg) {
          return driverLicenseFlg.name.replace("<br>", "");
        }
      }
      return "";
    },
    showDriverLicenseNum() {
      return this.userData.driver_license_flg === "1";
    },
    displayotherLoanCount() {
      return this.userData.other_loan_count + "件";
    },
    displayOtherLoanAmount() {
      return parseInt(this.userData.other_loan_amount / 10000) + "万円";
    },
    displayAskingLoanAmount() {
      for (let askingLoanAmount of askingLoanAmounts) {
        if (askingLoanAmount.value === this.userData.asking_loan_amount) {
          return askingLoanAmount.name;
        }
      }
      return "";
    },
  },
  mounted() {
    try {
      getUserDataLambdaInvoke()
        .then((res) => {
          for (let key in res) {
            if (Object.hasOwnProperty.call(this.userData, key)) {
              this.userData[key] = res[key];
            }
          }
          this.hasPhoneMobile = setHasPhoneMobile(
            this.userData.phone_mobile_1,
            this.userData.phone_mobile_2,
            this.userData.phone_mobile_3
          );
          this.$store.dispatch("setSystemAuId", res.system_auid);
        })
        .catch((error) => {
          goToErrorPage(error);
        });
      setTrackingEventDataSetting();
    } catch (e) {
      goToErrorPage(e);
    }
  },
  methods: {
    submitProfile: function () {
      if (this.isDisabled) {
        return;
      }
      this.isDisabled = true;
      initCredentials()
        .then(() => {
          submitAttributesLambdaInvoke()
            .then((res) => {
              this.errors = res.errors || {};
              if (Object.keys(this.errors).length > 0) {
                scrollTop();
                this.isDisabled = false;
              } else {
                let response = JSON.parse(res.body);
                // statusが3の場合は入会資格なしページへ遷移させる
                if (response.status === "3") {
                  this.$router.push({ name: "en023" });
                } else {
                  this.$router.push({ name: "en006", query: { state: "0" } });
                }
              }
            })
            .catch((error) => {
              goToErrorPage(error);
            });
        })
        .catch((error) => {
          goToErrorPage(error);
        });
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/stylesheets/styles";

.important {
  color: $danger;
}

.btn-txt {
  @include mq-down(md) {
    margin-top: 15px;
    margin-bottom: 35px;
  }
  @include mq-up(md) {
    margin-top: 15px;
    margin-bottom: 60px;
  }
}

.btn-main.-lg.confirmation {
  @include mq-down(md) {
    border-radius: 32px;
    width: 380px;
  }
}
</style>
